import { skipToken, useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import {
  GetUsersForTenantMemberSelectDocument,
  type GetUsersForTenantMemberSelectQuery,
  type GetUsersForTenantMemberSelectQueryVariables,
} from "~/features/admin/tenants/gqls";
import type { FilterUserOption } from "~/features/admin/tenants/types";

/**
 * Resolve the currently selected user-filter ids (from the URL) into full
 * user options so the filter chip can render names without re-searching.
 */
export function useSelectedFilterUsers(selectedUserIds: string[]): FilterUserOption[] {
  const { data } = useQuery<GetUsersForTenantMemberSelectQuery, GetUsersForTenantMemberSelectQueryVariables>(
    GetUsersForTenantMemberSelectDocument,
    selectedUserIds.length === 0 ? skipToken : { variables: { where: { id: { _in: selectedUserIds } } } },
  );

  return useMemo(() => {
    if (selectedUserIds.length === 0 || !data) return [];
    const byId = new Map(
      data.users.map(
        (u) =>
          [
            u.id,
            { id: u.id, displayName: u.displayName ?? null, email: u.email, deactivatedAt: u.deactivatedAt ?? null },
          ] as const,
      ),
    );
    return selectedUserIds.map((id) => byId.get(id)).filter((u): u is FilterUserOption => Boolean(u));
  }, [selectedUserIds, data]);
}
